import React, { useEffect, useRef, useState } from "react";
import { PackagePlus, Menu, X, Search, ShoppingCart, User, Headphones } from "lucide-react";
import { FiSearch } from "react-icons/fi";
import { toast } from "react-toastify";
import "./Navbar.css";
import {
  email,
  logo,
  whatsappImg,
} from "../../assets/imagesPath";
import { Link, NavLink, useNavigate } from "react-router-dom";
import SigninModal from "../allModals/SignupModal";
import PATHS from "../../routes/Paths";
import { useDispatch, useSelector } from "react-redux";
import LoginModal from "../allModals/loginModal";
import { userLogout, loggedIn } from "../../redux/AuthSlice";
import {
  useGetAllProductQuery,
  useViewCartOrdersQuery,
  useGetContactSettingsQuery,
} from "../../services/Api";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { setSearch } from "../../redux/ProductSlice";
import { useDebounce } from "use-debounce";
import VendorloginModal from "../allModals/VendorloginModal";
import VendorSignupModal from "../allModals/VendorSignupModal";
import RequestProductModal from "../allModals/RequestProductModal";
import TopNavbar from "./TopNavbar";

const Navbar = () => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showLoginModal2, setShowLoginModal2] = useState(false);
  const [showLoginModal3, setShowLoginModal3] = useState(false);
  const [showVendorSignupModal, setShowVendorSignupModal] = useState(false);
  const [showRequestProductModal, setShowRequestProductModal] = useState(false);
  const [name, setName] = useState("");
  const [debouncedName] = useDebounce(name, 300);
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [dismissedStatusBanner, setDismissedStatusBanner] = useState(false);
  const searchRef = useRef(null);
  const navigator = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector((data) => data.auth);
  const { isLoggedIn } = auth || {};
  // For vendors, user_id is stored separately; for normal users, it's in id field
  const ID = auth?.userDetail?.user_id || auth?.userDetail?.id;
  const { data: settingsData } = useGetContactSettingsQuery();
  const settings = settingsData?.status ? settingsData.data : {
    whatsapp_number: '+923000828456',
    phone_number: '+923000828456',
    email_address: 'easyqist@gmail.com'
  };

  const { data: viewCartOrders } = useViewCartOrdersQuery({
    params: { user_id: ID, order_status: 0 }
  });

  const signupModalHandler = () => {
    setShowLoginModal((prev) => !prev);
  };

  const requestProductModalHandler = () => {
    // Check vendor status before opening modal
    if (auth?.role === "vendor" && auth?.userDetail?.status) {
      const vendorStatus = auth.userDetail.status;
      
      if (vendorStatus === 'pending') {
        toast.warning("Your vendor application is under review. Approval may take up to 24 hours.", {
          autoClose: 5000
        });
        return;
      }
      
      if (vendorStatus === 'rejected') {
        const rejectionReason = auth.userDetail.rejection_reason || 'No reason provided';
        toast.error(`Your vendor application was rejected. Reason: ${rejectionReason}`, {
          autoClose: 7000
        });
        return;
      }
    }
    
    // Only open modal if vendor is approved or status check passed
    setShowRequestProductModal((prev) => !prev);
  };

  const loginModalHandler = () => {
    setShowLoginModal2((prev) => !prev);
  };

  const vendorLoginModalHandler = () => {
    setShowLoginModal3((prev) => !prev);
  };

  const vendorSignupModalHandler = () => {
    setShowVendorSignupModal((prev) => !prev);
  };

  const handleReapply = () => {
    // Open vendor signup modal for reapplication
    setShowVendorSignupModal(true);
  };

  const handleRefreshStatus = async () => {
    try {
      // Check if we have vendor ID or need to use phone as fallback
      const vendorId = auth?.userDetail?.id;
      const phoneNo = auth?.userDetail?.phone_no;
      
      // If ID looks like user_id (very large number), use phone instead
      const isUserIdNotVendorId = vendorId > 50000;
      
      if (!vendorId && !phoneNo) {
        toast.error("Unable to refresh status");
        return;
      }

      let updatedVendor = null;

      if (isUserIdNotVendorId && phoneNo) {
        // Fetch all vendors and find by phone
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}vendors`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        
        if (data.status && Array.isArray(data.data)) {
          updatedVendor = data.data.find(v => v.phone_no === phoneNo);
        }
      } else {
        // Fetch by vendor ID
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}vendor-details?id=${vendorId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        
        if (data.status && data.data?.vendor) {
          updatedVendor = data.data.vendor;
        }
      }

      if (updatedVendor) {
        // Update Redux store with fresh vendor data (preserve user_id if exists)
        const userData = {
          token: auth.token,
          user: {
            ...updatedVendor,
            user_id: auth.userDetail.user_id || updatedVendor.user_id
          },
          role: "vendor",
        };
        dispatch(loggedIn(userData));

        // Show appropriate message based on status
        if (updatedVendor.status === 'approved') {
          toast.success("Congratulations! Your application has been approved!");
          setDismissedStatusBanner(true);
        } else if (updatedVendor.status === 'rejected') {
          toast.error(`Your application was rejected. Reason: ${updatedVendor.rejection_reason || 'No reason provided'}`);
        } else {
          toast.info("Your application is still under review");
        }
      } else {
        toast.error("Unable to fetch vendor status");
      }
    } catch (error) {
      console.error("Refresh status error:", error);
      toast.error("Failed to refresh status. Please try again.");
    }
  };

  const toggleMobileSearch = () => {
    setShowMobileSearch(!showMobileSearch);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    // Close mobile search when menu opens
    if (!mobileMenuOpen) {
      setShowMobileSearch(false);
    }
  };

  const {
    refetch: getAllProductRefetch,
  } = useGetAllProductQuery(
    debouncedName
      ? { params: { start: 0, limit: 30, name: debouncedName }, refetchOnMountOrArgChange: false }
      : skipToken
  );

  useEffect(() => {
    const fetchData = async () => {
      if (debouncedName) {
        const { data } = await getAllProductRefetch();
        const filteredProducts = data?.data?.filter(
          (product) => product != null
        );
        setSearchResults(filteredProducts || []);
        setShowResults(true);
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    };
    fetchData();
  }, [debouncedName]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      {/* Top Navbar - Only show when logged in */}
      {isLoggedIn && <TopNavbar />}

      {/* Vendor Status Alert Banner */}
      {isLoggedIn && auth?.role === "vendor" && auth?.userDetail?.status && !dismissedStatusBanner && (
        <>
          {auth.userDetail.status === 'pending' && (
            <div style={{
              backgroundColor: '#FEF3C7',
              borderBottom: '2px solid #F59E0B',
              padding: '12px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              color: '#92400E',
              fontWeight: '500',
              position: 'relative'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap', justifyContent: 'center' }}>
                <span>
                  <i className="fas fa-clock me-2"></i>
                  Your vendor application is under review. Approval may take up to 24 hours.
                </span>
                <button
                  onClick={handleRefreshStatus}
                  style={{
                    padding: '4px 12px',
                    backgroundColor: '#F59E0B',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500'
                  }}
                >
                  Refresh Status
                </button>
              </div>
              <button
                onClick={() => setDismissedStatusBanner(true)}
                style={{
                  position: 'absolute',
                  right: '15px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  color: '#92400E',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
          )}
          
          {auth.userDetail.status === 'rejected' && (
            <div style={{
              backgroundColor: '#FEE2E2',
              borderBottom: '2px solid #DC2626',
              padding: '12px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              color: '#991B1B',
              fontWeight: '500',
              position: 'relative'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap', justifyContent: 'center' }}>
                <span>
                  <i className="fas fa-exclamation-circle me-2"></i>
                  Your vendor application was rejected.
                  {auth.userDetail.rejection_reason && (
                    <span> Reason: <strong>{auth.userDetail.rejection_reason}</strong></span>
                  )}
                </span>
                <button
                  onClick={handleReapply}
                  style={{
                    padding: '4px 12px',
                    backgroundColor: '#DC2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500'
                  }}
                >
                  Reapply
                </button>
              </div>
              <button
                onClick={() => setDismissedStatusBanner(true)}
                style={{
                  position: 'absolute',
                  right: '15px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  color: '#991B1B',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
          )}
        </>
      )}

      {/* Combined Main Navbar */}
      <nav className="combined-navbar">
        <div className="navbar-container">
          {/* Logo Section */}
          <div className="navbar-logo">
            <img
              src={logo}
              className="brand-logo cursor"
              onClick={() => navigator(PATHS.home)}
              alt="Logo"
            />
          </div>

          {/* Search Bar Section - Hidden on mobile by default */}
          <div
            className={`navbar-search-section ${showMobileSearch ? "mobile-search-active" : ""
              }`}
          >
            <div className="search-container" ref={searchRef}>
              <input
                type="text"
                className="search-input"
                placeholder="Search products..."
                value={name}
                onChange={(e) => setName(e.target.value)}
                onFocus={() => searchResults.length > 0 && setShowResults(true)}
              />
              <FiSearch className="search-icon" aria-label="Search" />

              {showResults && searchResults?.length > 0 && (
                <ul className="search-results">
                  {searchResults?.map((product) => (
                    <li
                      key={product?.id}
                      className="search-result-item"
                      onClick={() => {
                        navigator(`/productDetails/${product?.id}`);
                        setShowResults(false);
                        setName("");
                      }}
                    >
                      {product?.name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Desktop Navigation Links */}
          <div className="navbar-nav-desktop">
            <ul className="nav-links">
              <li>
                <NavLink to={PATHS.home} className="nav-link">
                  Home
                </NavLink>
              </li>
              <li>
                <NavLink to={PATHS.shop} className="nav-link">
                  Shop
                </NavLink>
              </li>
              <li>
                <NavLink to={PATHS.brands} className="nav-link">
                  Brands
                </NavLink>
              </li>
              <li>
                <NavLink to={PATHS.about} className="nav-link">
                  About
                </NavLink>
              </li>
              <li>
                <NavLink to={PATHS.contactUs} className="nav-link">
                  Contact
                </NavLink>
              </li>
              <li>
                <NavLink to={PATHS.blogs} className="nav-link">
                  Blogs
                </NavLink>
              </li>
            </ul>
          </div>

          {/* Right Section - Cart, Auth, Actions */}
          <div className="navbar-right-section">
            {/* Mobile Search Toggle Button - Only show on mobile */}
            <button
              className="mobile-search-toggle"
              onClick={toggleMobileSearch}
              aria-label="Toggle search"
            >
              <Search size={20} />
            </button>

            {/* Cart - Only show when user is logged in */}
            {isLoggedIn && (
              <div
                className="cart-container"
                onClick={() => navigator(PATHS.cart)}
              >
                <div className="cart-icon-wrapper">
                  <ShoppingCart className="cart-icon" aria-label="Cart" />
                  {viewCartOrders?.data?.order_products?.length > 0 && (
                    <span className="cart-badge">
                      {viewCartOrders?.data?.order_products?.length}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Desktop Auth Section - Only show when NOT logged in */}
            {!isLoggedIn && (
              <div className="auth-section desktop-auth">
                <div className="auth-buttons">
                  <button
                    className="auth-btn login-btn"
                    onClick={loginModalHandler}
                  >
                    Login
                  </button>
                  <button
                    className="auth-btn vendor-btn"
                    onClick={vendorLoginModalHandler}
                  >
                    Vendor Login
                  </button>
                  <button
                    className="auth-btn vendor-signup-btn"
                    onClick={vendorSignupModalHandler}
                  >
                    Vendor Signup
                  </button>
                  <button
                    className="auth-btn signup-btn"
                    onClick={signupModalHandler}
                  >
                    Signup
                  </button>
                </div>
              </div>
            )}

            {/* Mobile Auth Buttons - Only show when NOT logged in */}
            {!isLoggedIn && (
              <div className="mobile-auth-buttons-top">
                <button
                  className="mobile-auth-btn-top login-btn"
                  onClick={loginModalHandler}
                >
                  Login
                </button>
                <button
                  className="mobile-auth-btn-top vendor-btn"
                  onClick={vendorLoginModalHandler}
                >
                  Vendor
                </button>
                <button
                  className="mobile-auth-btn-top signup-btn"
                  onClick={signupModalHandler}
                >
                  Signup
                </button>
              </div>
            )}

            {/* User Section - Only show when logged in */}
            {isLoggedIn && (
              <div className="user-section">
                {/* Profile Dropdown */}
                <div className="profile-dropdown">
                  <button
                    className="profile-button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <User className="profile-icon" aria-label="Profile" />
                  </button>
                  <ul className="dropdown-menu profile-menu">
                    <li>
                      <Link to={PATHS.profile} className="dropdown-item">
                        Profile
                      </Link>
                    </li>
                    <li>
                      <hr className="dropdown-divider" />
                    </li>
                    <li>
                      <button
                        className="dropdown-item logout-item"
                        onClick={() => dispatch(userLogout())}
                      >
                        Logout
                      </button>
                    </li>
                  </ul>
                </div>

                {/* Vendor Request Button */}
                {auth?.role === "vendor" && (
                  <button
                    className="vendor-request-button"
                    onClick={requestProductModalHandler}
                  >
                    <PackagePlus size={18} className="vendor-icon" />
                    <span className="vendor-text">Request Product</span>
                  </button>
                )}
              </div>
            )}

            {/* Contact Info Desktop */}
            <div className="contact-info-desktop">
              <Headphones className="contact-icon" aria-label="Contact" />
              <div className="contact-text-wrapper">
                <span className="contact-label">Contact Us</span>
                <span className="contact-number">{settings.phone_number}</span>
              </div>
            </div>

            {/* Mobile Menu Toggle */}
            <button className="mobile-menu-toggle" onClick={toggleMobileMenu}>
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Contact Info */}
        <div className="mobile-contact-info">
          <div className="contact-item">
            <img src={whatsappImg} className="contact-icon" alt="WhatsApp" />
            <span className="contact-text">{settings.whatsapp_number}</span>
          </div>
          {settings.email_address && (
            <div className="contact-item">
              <img src={email} className="contact-icon" alt="Email" />
              <span className="contact-text">{settings.email_address}</span>
            </div>
          )}
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="mobile-menu">
            <div className="mobile-nav-links">
              <NavLink
                to={PATHS.home}
                className="mobile-nav-link"
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </NavLink>
              <NavLink
                to={PATHS.shop}
                className="mobile-nav-link"
                onClick={() => setMobileMenuOpen(false)}
              >
                Shop
              </NavLink>
              <NavLink
                to={PATHS.brands}
                className="mobile-nav-link"
                onClick={() => setMobileMenuOpen(false)}
              >
                Brands
              </NavLink>
              <NavLink
                to={PATHS.about}
                className="mobile-nav-link"
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </NavLink>
              <NavLink
                to={PATHS.contactUs}
                className="mobile-nav-link"
                onClick={() => setMobileMenuOpen(false)}
              >
                Contact
              </NavLink>
              <NavLink
                to={PATHS.blogs}
                className="mobile-nav-link"
                onClick={() => setMobileMenuOpen(false)}
              >
                Blogs
              </NavLink>

              {isLoggedIn && (
                <>
                  <NavLink
                    to={PATHS.profile}
                    className="mobile-nav-link"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Profile
                  </NavLink>
                  <button
                    className="mobile-nav-link logout-link"
                    onClick={() => {
                      dispatch(userLogout());
                      setMobileMenuOpen(false);
                    }}
                  >
                    Logout
                  </button>
                </>
              )}
            </div>

            {/* Mobile Auth Buttons in Menu (when not logged in) */}
            {!isLoggedIn && (
              <div className="mobile-auth-section">
                <button
                  className="mobile-auth-btn login-btn"
                  onClick={() => {
                    loginModalHandler();
                    setMobileMenuOpen(false);
                  }}
                >
                  Login
                </button>
                <button
                  className="mobile-auth-btn vendor-btn"
                  onClick={() => {
                    vendorLoginModalHandler();
                    setMobileMenuOpen(false);
                  }}
                >
                  Vendor Login
                </button>
                <button
                  className="mobile-auth-btn vendor-signup-btn"
                  onClick={() => {
                    vendorSignupModalHandler();
                    setMobileMenuOpen(false);
                  }}
                >
                  Vendor Signup
                </button>
                <button
                  className="mobile-auth-btn signup-btn"
                  onClick={() => {
                    signupModalHandler();
                    setMobileMenuOpen(false);
                  }}
                >
                  Sign Up
                </button>
              </div>
            )}

            {/* Vendor Request in Mobile */}
            {isLoggedIn && auth?.role === "vendor" && (
              <div className="mobile-vendor-section">
                <button
                  className="mobile-vendor-btn"
                  onClick={() => {
                    requestProductModalHandler();
                    setMobileMenuOpen(false);
                  }}
                >
                  <PackagePlus size={18} />
                  Request Product
                </button>
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Modals */}
      {showLoginModal && (
        <SigninModal handleCloseSignupModal={signupModalHandler} />
      )}

      {showLoginModal2 && (
        <LoginModal handleCloseSignupModal={loginModalHandler} />
      )}

      {showLoginModal3 && (
        <VendorloginModal
          handleCloseVendorloginModal={vendorLoginModalHandler}
        />
      )}

      {showVendorSignupModal && (
        <VendorSignupModal
          handleCloseVendorSignupModal={vendorSignupModalHandler}
        />
      )}

      {showRequestProductModal && (
        <RequestProductModal
          handleCloseRequestProductModal={requestProductModalHandler}
        />
      )}
    </>
  );
};

export default Navbar;
